<template>
  <el-dialog
    :title="isNew ? text.agencyAddAction : text.agencyEditAction"
    :visible="visible"
    width="520px"
    @update:visible="$emit('update:visible', $event)">
    <el-form label-width="110px" size="small">
      <el-form-item :label="text.operatorLabel">
        <span class="agency-form__operator">{{ operatorLabel }}</span>
      </el-form-item>
      <!-- 코드는 등록에서만 받는다 — 수정 본문에 `code` 가 없다(§3-3) -->
      <el-form-item v-if="isNew" :label="text.agencyCodeLabel" :error="formError.code">
        <el-input v-model="form.code" class="agency-form__code" maxlength="12" clearable @input="previewCode" />
        <div class="agency-form__hint">{{ text.agencyCodeAutoHint }}</div>
        <!-- 정규화는 서버가 한다 — 화면은 `agencies/search` 가 그 질의어를 어떤 코드로 읽었는지 보인다 -->
        <div v-if="codePreview" class="agency-form__preview">{{ codePreview }}</div>
      </el-form-item>
      <el-form-item :label="text.agencyNmLabel" :error="formError.agencyNm">
        <el-input v-model="form.agencyNm" class="agency-form__name" maxlength="60" clearable />
      </el-form-item>
      <el-form-item :label="text.mgrNmLabel" :error="formError.mgrNm">
        <el-input v-model="form.mgrNm" maxlength="30" clearable />
      </el-form-item>
      <el-form-item :label="text.mobileLabel" :error="formError.mobile">
        <el-input
          v-keep-number-caret
          :value="util.telFieldText(form.mobile)"
          class="agency-form__mobile"
          maxlength="20"
          clearable
          @input="form.mobile = util.telDigits($event)" />
      </el-form-item>
      <el-form-item :label="text.memoLabel">
        <el-input v-model="form.memo" type="textarea" :rows="2" maxlength="200" />
      </el-form-item>
    </el-form>

    <p v-if="serverMessage" class="agency-form__error">{{ serverMessage }}</p>
    <!-- 같은 운영사의 비활성 관리점 담당자 번호(409) — 새로 만들지 말고 그 관리점을 되살리는 길(R-A §1-3).
         재활성 권한이 없는 역할에게는 문장만 남는다 -->
    <p v-if="reactivateTarget && canReactivate" class="agency-form__reactivate">
      <el-button size="mini" type="primary" plain @click="$emit('reactivate', reactivateTarget)">{{
        reactivateTarget.actionText
      }}</el-button>
    </p>

    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ text.cancelAction }}</el-button>
      <el-button size="small" type="primary" :loading="saving" @click="save">{{ text.saveAction }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { HttpError, mergeErrors } from '@/plugins/http'
import { permittedRead } from '@/plugins/http/permittedRead'
import { httpErrorBody, httpErrorMessage } from '@/plugins/http/policy'
import {
  AGENCY_LOOKUP_READ,
  CALLPOP_ACTION,
  CALLPOP_FORM_WRITE_CONFIG,
  agencyOptionsOf,
  agencyUrl,
  inactiveAgencyConflictOf,
  itemsOf,
  orgLabel,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { latestGuard } from '@/utils/latestGuard'

/**
 * 관리점 등록·수정.
 *
 * - **코드를 비우면 서버가 자동 채번한다**(`MAX(serial)+1`, §3-3). 비우는 것이 기본 용법이다.
 * - 코드를 직접 넣으면 **서버가 그 질의어를 어떤 코드로 읽는지** 미리 보인다(`GET /callpop/agencies/search`).
 *   정규화 규칙(`cj-0001` → `CJ0001`)은 서버가 정본이라 화면에 사본을 두지 않는다 — 여기 있는 것은
 *   「서버가 뭐라고 답했나」뿐이다. 그 조회는 **활성 관리점만** 보므로 답이 없으면(0행) 「활성 중에는 없다」고만
 *   말하고, 비활성 관리점과 겹치는지는 저장의 409 가 답한다(`CALLPOP_TEXT.agencyCodePreviewNone`).
 * - 접두 불일치·중복·휴대폰 유일은 저장할 때 서버가 400·409 로 답하고 그 문장이 그대로 폼에 앉는다.
 * - 409 `AGENCY_INACTIVE_SAME_MOBILE`(같은 운영사의 비활성 관리점 담당자 번호)이면 그 관리점의 「재활성」
 *   버튼을 함께 세운다. 누르면 `reactivate` 를 올린다 — 확인창과 호출은 목록 행의 재활성과 같은 자리(부모)다.
 */
export default {
  name: 'AgencyFormDialog',

  props: {
    visible: { type: Boolean, default: false },
    /** 등록할 운영사 — 등록·수정 모두 바꿀 수 없다(수정 본문에 `operatorCd` 가 없다) */
    operator: { type: Object, default: null },
    /** `null` 이면 등록 */
    agency: { type: Object, default: null },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      form: this.initForm(null),
      formError: this.initFormError(),
      serverMessage: '',
      /** 등록 409 가 가리킨 비활성 관리점(`inactiveAgencyConflictOf`) — 없으면 `null` */
      reactivateTarget: null,
      codePreview: '',
      /** 늦게 온 미리 보기가 최신 입력의 답을 덮지 못하게(`utils/latestGuard`) */
      previewGuard: latestGuard(),
      saving: false,
    }
  },

  computed: {
    isNew() {
      return !this.agency
    },
    operatorLabel() {
      return orgLabel(this.operator && this.operator.operatorNm, this.operator && this.operator.code)
    },
    /** 재활성 버튼의 자격 — 목록 행의 재활성 버튼과 같은 액션이다(`AgencyTable.can.on`) */
    canReactivate() {
      return checkAuthActionAccess(CALLPOP_ACTION.AGENCY_ON)
    },
  },

  watch: {
    visible(open) {
      if (!open) {
        if (this._timer) clearTimeout(this._timer)

        return
      }
      this.form = this.initForm(this.agency)
      this.formError = this.initFormError()
      this.serverMessage = ''
      this.reactivateTarget = null
      this.codePreview = ''
    },
  },

  beforeDestroy() {
    if (this._timer) clearTimeout(this._timer)
    this.previewGuard.close()
  },

  methods: {
    initForm(row) {
      return {
        code: (row && row.code) || '',
        agencyNm: (row && row.agencyNm) || '',
        mgrNm: (row && row.mgrNm) || '',
        mobile: (row && row.mobile) || '',
        memo: (row && row.memo) || '',
      }
    },

    initFormError() {
      return { code: '', agencyNm: '', mgrNm: '', mobile: '' }
    },

    /** 코드 미리 보기 — 입력할 때마다 부르므로 눌러 담는다(300ms) */
    previewCode(value) {
      if (this._timer) clearTimeout(this._timer)
      if (!value || !String(value).trim()) {
        this.codePreview = ''

        return
      }
      this._timer = setTimeout(() => this.loadCodePreview(String(value).trim()), 300)
    },

    async loadCodePreview(query) {
      const seq = this.previewGuard.begin()

      try {
        const outcome = await permittedRead(Object.assign({}, AGENCY_LOOKUP_READ, { params: { q: query } }))
        if (!this.previewGuard.isLatest(seq)) return

        if (!outcome.allowed) {
          this.codePreview = ''

          return
        }
        const found = agencyOptionsOf(itemsOf(outcome.data))
        // 0행은 「없다」가 아니라 **「이 조회로는 못 찾았다」**이다 — 그 조회는 내 범위 안 활성 관리점만
        // 본다. 정규화된 형태도 화면이 지어내지 않는다(사본 금지) — 유일성은 저장할 때 서버가 답한다
        this.codePreview =
          found.length === 1
            ? `${this.text.agencyCodePreviewTaken}: ${found[0].label}`
            : found.length === 0
            ? this.text.agencyCodePreviewNone
            : ''
      } catch (error) {
        if (this.previewGuard.isLatest(seq)) this.codePreview = ''
      }
    },

    /** 저장 본문 — 수정에는 `code`·`operatorCd` 가 없다(서버가 받지 않는 칸이다) */
    payload() {
      const body = {
        agencyNm: this.form.agencyNm,
        mgrNm: this.form.mgrNm,
        mobile: this.form.mobile,
        memo: this.form.memo || null,
      }
      if (this.isNew) {
        body.operatorCd = this.operator ? this.operator.operatorCd : null
        // 비우면 자동 채번 — 빈 문자열이 아니라 `null` 로 보낸다(「값이 있다」는 다른 뜻이다)
        body.code = this.form.code && this.form.code.trim() ? this.form.code.trim() : null
      }

      return body
    },

    async save() {
      this.formError = this.initFormError()
      this.serverMessage = ''
      this.reactivateTarget = null
      this.saving = true

      try {
        const url = this.isNew ? agencyUrl() : agencyUrl(this.agency.agencyCd)
        const response = this.isNew
          ? await this.$axios.post(url, this.payload(), CALLPOP_FORM_WRITE_CONFIG)
          : await this.$axios.put(url, this.payload(), CALLPOP_FORM_WRITE_CONFIG)

        this.$emit('saved', response.data)
        this.$emit('update:visible', false)
      } catch (error) {
        if (error instanceof HttpError && error.messages) mergeErrors(this.formError, error.messages)
        this.serverMessage = httpErrorMessage(error, this.text.requiredFieldMissing)
        // 수정(`PUT`)에는 이 사유가 없다 — 서버가 새 코드를 만들 때만 싣는다
        this.reactivateTarget = inactiveAgencyConflictOf(httpErrorBody(error))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.agency-form__hint,
.agency-form__preview {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}
.agency-form__preview {
  color: #409eff;
}
.agency-form__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 0 0 4px;
}
.agency-form__reactivate {
  margin: 4px 0 0;
}
</style>
